import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, Unlock, Sparkles, Zap, Layers } from "lucide-react";

/**
 * Plan · Enabled Modules — tenant-facing.
 * Shows what's on, what's off, and a "Request enable" CTA for the disabled ones.
 */
export default function PlanPage() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);   // module key being requested
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/plan/status");
      setData(data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      await api.post("/plan/request-enable", { module: dialog, reason });
      toast.success("Request sent to your admin");
      setDialog(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-sm text-[#4A5A50]">Loading…</div>;

  const pendingSet = new Set((data.pending_requests || []).map((r) => r.module));

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="plan-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-[#083F22] text-white flex items-center justify-center"><Layers size={18}/></div>
        <div className="flex-1">
          <div className="font-serif text-2xl">Your Plan</div>
          <div className="text-xs text-[#4A5A50]">{data.enabled_count} of {data.total_count} modules enabled for {data.company?.name}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {data.modules.map((m) => {
          const requested = pendingSet.has(m.key);
          return (
            <Card key={m.key} className={m.enabled ? "border-[#0F5E39]/40" : "border-[#E4DFCF] bg-[#F6F1E1]/40"}>
              <CardContent className="p-4 flex items-start gap-3" data-testid={`plan-module-${m.key}`}>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${m.enabled ? "bg-[#0F5E39] text-white" : "bg-[#E4DFCF] text-[#8A6600]"}`}>
                  {m.enabled ? <Unlock size={16}/> : <Lock size={16}/>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-serif text-lg">{m.label}</div>
                    {m.enabled
                      ? <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E1F0E6] text-[#0F5E39]">enabled</span>
                      : <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F1EDDF] text-[#8A6600]">disabled</span>}
                    {requested && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFF6E1] text-[#8A6600]">requested</span>}
                  </div>
                  <div className="text-xs text-[#4A5A50] mt-1">{m.description}</div>
                </div>
                {!m.enabled && !requested && (
                  <Button
                    size="sm"
                    className="rounded-full bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15]"
                    onClick={() => setDialog(m.key)}
                    data-testid={`plan-request-${m.key}`}
                  >
                    <Sparkles size={14} className="mr-1"/>Request enable
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {dialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-[#FAF7EF] rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="plan-request-dialog">
            <div className="font-serif text-xl mb-1">Request "{data.modules.find((m) => m.key === dialog)?.label}"</div>
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-3">Tell us why you need this module</div>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional…" data-testid="plan-request-reason"/>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Cancel</Button>
              <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={submit} data-testid="plan-request-submit">Send request</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
